import type { Nocturn, Session, User } from './client';

/**
 * The parts of signing in that must not differ between web and mobile.
 *
 * Everything here is either pure or a single supabase-js call, and it lives in
 * one place for the same reason `getTier` does: two implementations of "did
 * that sign-up actually work" drift, and the way they drift is that one client
 * starts telling people an email is on its way when none was sent.
 */

/**
 * Addresses arrive with whitespace and capitals attached — iOS autocapitalises
 * the first letter, autofill pastes a trailing space, someone types their
 * address with a capital initial.
 *
 * Supabase stores and matches the address lowercased, so `Sam@x.com` signs up
 * fine and then fails to sign in against a form that sent it verbatim. The
 * trailing space is worse: it fails validation and the message is just
 * "Unable to validate email address: invalid format", pointing at nothing
 * visible on screen.
 */
export function normaliseEmail(raw: string): string {
  return raw.trim().toLowerCase();
}

/** Same treatment for names, which ride along into profiles as metadata. */
export function normaliseName(raw: string): string {
  return raw.trim().replace(/\s+/g, ' ');
}

/**
 * The shortest password the backend will take.
 *
 * Supabase's own floor is 6 and configurable per project; asking for 8 in the
 * form is a deliberate step up, and it is stated on screen rather than only
 * enforced, so nobody discovers it by being rejected.
 */
export const MIN_PASSWORD = 8;

/**
 * Did `signUp` quietly decline because the address already has an account?
 *
 * With email confirmation on, Supabase deliberately does not say so — telling
 * an anonymous form which addresses are registered is a user-enumeration hole.
 * It returns 200 with an obfuscated user, no session, and, the only usable
 * tell, an empty `identities` array.
 *
 * Without this check the sign-up branch reads "no session, so confirmation is
 * required" and promises an email that Supabase never sent, which strands a
 * returning member on a form that will never do anything.
 */
export function isExistingAccount(data: {
  user: User | null;
  session: Session | null;
}): boolean {
  return Boolean(data.user && !data.session && (data.user.identities?.length ?? 0) === 0);
}

/** Codes gotrue returns. Older deployments send only a message, hence the fallbacks. */
const MESSAGES: Record<string, string> = {
  invalid_credentials:
    'That email and password do not match an account. Check the password, or use “Email me a link instead”.',
  email_not_confirmed:
    'This account still needs confirming. Open the link in the email we sent, or send yourself a fresh one below.',
  email_exists: 'There is already an account on that address. Sign in instead.',
  user_already_exists: 'There is already an account on that address. Sign in instead.',
  weak_password: `Pick a longer password — at least ${MIN_PASSWORD} characters.`,
  same_password: 'That is the password you already have. Choose a different one.',
  otp_expired:
    'That link has expired. Links last an hour and work once; send yourself a new one.',
  // Neither of these is necessarily about the person reading it. The email cap
  // is per project and per hour, not per address -- on the default mailer it is
  // two an hour for everybody -- so the second person to sign up in an hour is
  // refused having done nothing at all. Telling them they have tried too many
  // times is both wrong and unactionable, and "wait a minute" is out by an
  // hour. See the email delivery section of the README.
  over_email_send_rate_limit:
    'We could not send that email just now — the limit is ours, not yours. Try again shortly, and tell us if it keeps happening.',
  over_request_rate_limit: 'Too many attempts just now. Wait a minute and try again.',
  validation_failed: 'Check the email address — that one is not a valid format.',
  signup_disabled: 'New accounts are closed at the moment.',
  user_banned: 'This account is suspended. Get in touch and we will sort it out.',
};

/**
 * A message worth showing someone.
 *
 * gotrue's own strings are written for developers: "Invalid login credentials"
 * does not tell a member whether they mistyped the password or never had an
 * account, and a failed request surfaces as a bare "Failed to fetch" with no
 * status, because the browser withholds cross-origin detail on purpose.
 */
export function authMessage(err: unknown): string {
  if (!err) return 'Something went wrong. Try again.';

  const code = typeof err === 'object' && err !== null ? (err as { code?: string }).code : undefined;
  if (code && MESSAGES[code]) return MESSAGES[code];

  const raw = err instanceof Error ? err.message : String(err);

  // No status and no detail: DNS, offline, or an extension blocking the call.
  if (err instanceof TypeError || /failed to fetch|network request failed/i.test(raw)) {
    return 'Could not reach the sign-in service. Check your connection and try again.';
  }

  // Deployments predating error codes, matched on the text they do send.
  if (/invalid login credentials/i.test(raw)) return MESSAGES.invalid_credentials;
  if (/email not confirmed/i.test(raw)) return MESSAGES.email_not_confirmed;
  if (/already registered|already exists/i.test(raw)) return MESSAGES.email_exists;
  if (/password should be at least/i.test(raw)) return MESSAGES.weak_password;
  if (/for security purposes|rate limit|too many requests/i.test(raw)) {
    return MESSAGES.over_request_rate_limit;
  }
  if (/expired|invalid.*token/i.test(raw)) return MESSAGES.otp_expired;
  if (/unable to validate email/i.test(raw)) return MESSAGES.validation_failed;

  return raw;
}

/** True when the account exists but has never confirmed its address. */
export function needsConfirmation(err: unknown): boolean {
  const code = typeof err === 'object' && err !== null ? (err as { code?: string }).code : undefined;
  if (code === 'email_not_confirmed') return true;
  const raw = err instanceof Error ? err.message : String(err ?? '');
  return /email not confirmed/i.test(raw);
}

export type AuthCallback =
  | { kind: 'tokens'; accessToken: string; refreshToken: string }
  | { kind: 'code'; code: string }
  | { kind: 'error'; message: string };

/**
 * What a sign-in link carries when it lands back on the app.
 *
 * The browser never needs this: supabase-js reads `window.location` itself
 * when `detectSessionInUrl` is on. React Native has no location to read, so
 * the deep link arrives as a plain string and something has to open it —
 * otherwise the magic link opens the app and leaves the person exactly as
 * signed out as they were, which is the whole of mobile sign-in failing with
 * no error anywhere.
 *
 * Both shapes are handled because which one arrives is a project setting, not
 * a property of this code: the implicit flow puts tokens in the fragment, PKCE
 * puts a single-use `code` in the query.
 */
export function parseAuthCallback(url: string | null | undefined): AuthCallback | null {
  if (!url) return null;

  // Not `new URL()`: Expo hands back `exp://127.0.0.1:8081/--/account#...` in
  // development, and custom schemes parse inconsistently across polyfills.
  const [beforeHash, hash = ''] = url.split('#');
  const queryStart = beforeHash.indexOf('?');
  const query = queryStart === -1 ? '' : beforeHash.slice(queryStart + 1);

  // Fragment first: the implicit flow puts the tokens there, and a stale
  // `code` left in the query should never outrank a live token.
  const params = new URLSearchParams(hash);
  for (const [key, value] of new URLSearchParams(query)) {
    if (!params.has(key)) params.set(key, value);
  }

  const error = params.get('error_description') ?? params.get('error');
  if (error) return { kind: 'error', message: error.replace(/\+/g, ' ') };

  const accessToken = params.get('access_token');
  const refreshToken = params.get('refresh_token');
  if (accessToken && refreshToken) return { kind: 'tokens', accessToken, refreshToken };

  const code = params.get('code');
  if (code) return { kind: 'code', code };

  return null;
}

/**
 * Turn a returning sign-in link into a session.
 *
 * Returns null when the URL is an ordinary deep link with no auth on it, which
 * is most of them — every push notification and shared bar link comes through
 * the same handler.
 */
export async function completeSignInFromUrl(
  db: Nocturn,
  url: string | null | undefined,
): Promise<Session | null> {
  const callback = parseAuthCallback(url);
  if (!callback) return null;

  if (callback.kind === 'error') throw new Error(callback.message);

  if (callback.kind === 'tokens') {
    const { data, error } = await db.auth.setSession({
      access_token: callback.accessToken,
      refresh_token: callback.refreshToken,
    });
    if (error) throw error;
    return data.session;
  }

  const { data, error } = await db.auth.exchangeCodeForSession(callback.code);
  if (error) throw error;
  return data.session;
}

/* ------------------------------------------------------------------ identity */

/**
 * Which ways this account can be signed into.
 *
 * Read from `app_metadata`, which rides in the JWT and is therefore always
 * present, rather than from `user.identities`, which is only populated on
 * responses that went to the server for it.
 */
export function providersFor(user: User | null | undefined): string[] {
  if (!user) return [];
  const meta = user.app_metadata as { provider?: string; providers?: string[] } | undefined;
  const all = meta?.providers ?? (meta?.provider ? [meta.provider] : []);
  return all.filter((p): p is string => typeof p === 'string');
}

/** True when an address and password is one of the ways in. */
export function isEmailAccount(user: User | null | undefined): boolean {
  return providersFor(user).includes('email');
}

/**
 * Has this address been proved?
 *
 * Google and Apple assert it themselves, so a social account is verified from
 * the first moment. Only an email signup can be sitting here unproved, which
 * is exactly the case that has to be stopped.
 */
export function emailVerified(user: User | null | undefined): boolean {
  if (!user) return false;
  // confirmed_at covers older projects, where the column was not yet split.
  return Boolean(user.email_confirmed_at ?? (user as { confirmed_at?: string }).confirmed_at);
}

/** Can this account be signed into with a password? Server's answer, not a guess. */
export async function hasPassword(db: Nocturn): Promise<boolean> {
  const { data, error } = await db.rpc('has_password');
  // Treated as "yes" on failure, deliberately: the consequence of a wrong
  // "no" is nagging somebody who already has a password to set another one.
  return error ? true : Boolean(data);
}

export type ProfileNames = {
  first_name: string | null;
  last_name: string | null;
  display_name: string | null;
  email: string | null;
};

export async function getProfileNames(db: Nocturn): Promise<ProfileNames | null> {
  const { data } = await db
    .from('profiles')
    .select('first_name, last_name, display_name, email')
    .maybeSingle();
  return (data as ProfileNames | null) ?? null;
}

/**
 * Write the name onto the profile.
 *
 * Needed as a client write, rather than left to the signup trigger, because of
 * Apple: it hands over a name on the first authorisation and never again, and
 * on iOS that name arrives in the native credential rather than in anything
 * Supabase stores. If it is not written down at that moment it is gone.
 *
 * display_name is set alongside so the two cannot disagree — every list in the
 * product reads display_name, and a first and last name that never reached it
 * would leave the member still showing as the front half of their address.
 */
export async function saveProfileNames(
  db: Nocturn,
  userId: string,
  first: string,
  last: string,
): Promise<void> {
  const first_name = normaliseName(first);
  const last_name = normaliseName(last);
  const display_name = [first_name, last_name].filter(Boolean).join(' ');

  const { error } = await db
    .from('profiles')
    .update({ first_name, last_name, ...(display_name ? { display_name } : {}) })
    .eq('id', userId);

  if (error) throw error;
}

/**
 * Delete the signed-in account, for good.
 *
 * The `delete_account()` function used to remove the caller's avatar files
 * with a direct `delete from storage.objects`. Supabase now refuses that --
 * storage deletes have to go through its own API so the object in the bucket
 * is actually dropped, not just the metadata row -- and the SQL delete fails
 * outright with "use the storage API instead". So that half moved here,
 * under the same "members remove their own avatar" policy the upload button
 * already relies on, before the database is asked to remove everything else
 * that hangs off the account.
 *
 * Best-effort on the avatar: a failure to list or remove it must not be why
 * the account itself cannot be deleted. An orphaned file in a public bucket
 * is a far smaller problem than a delete button that does not work.
 */
export async function deleteAccount(db: Nocturn): Promise<void> {
  const uid = (await db.auth.getUser()).data.user?.id;

  if (uid) {
    try {
      const { data: files } = await db.storage.from('avatars').list(uid);
      const paths = (files ?? []).map((f) => `${uid}/${f.name}`);
      if (paths.length > 0) await db.storage.from('avatars').remove(paths);
    } catch {
      // Best-effort. See above.
    }
  }

  const { error } = await db.rpc('delete_account');
  if (error) throw error;
}

/**
 * Fill in a name the provider gave us that the database could not have seen.
 *
 * Only ever writes into blanks: a member who has corrected their own name must
 * not have it replaced by whatever Apple had on file in 2019.
 */
export async function backfillProfileNames(
  db: Nocturn,
  userId: string,
  first: string | null | undefined,
  last: string | null | undefined,
): Promise<void> {
  if (!normaliseName(first ?? '') && !normaliseName(last ?? '')) return;

  const existing = await getProfileNames(db);
  if (existing?.first_name?.trim() || existing?.last_name?.trim()) return;

  await saveProfileNames(db, userId, first ?? '', last ?? '');
}

/**
 * What this account still has to do before it is a usable membership.
 *
 * Ordered, and the order is the argument:
 *
 * - `verify` — an email signup that has not proved the address. It comes first
 *   because everything else is wasted effort if the address is somebody
 *   else's, and because the drinks announcement is sent by email.
 * - `age` — no date of birth on file. Second, because Nocturn is a free-drink
 *   app and an account that cannot clear this is not going to use the rest of
 *   what it is about to set up. Asked here, once, rather than to every visitor
 *   on the way in the door.
 * - `password` — a magic-link account with no password. The member code screen
 *   is used standing at a bar with the email app a context switch away, which
 *   is the worst possible place to discover your only way in is a link.
 * - `name` — no first or last name, which happens when a provider withholds
 *   one. A bartender confirming a drink has nothing to check against an ID.
 */
export type AccountStep = 'verify' | 'age' | 'password' | 'name' | 'username' | 'ready';

export function accountStep(input: {
  user: User | null;
  hasPassword: boolean;
  names: ProfileNames | null;
  ageConfirmed: boolean;
  username?: string | null;
}): AccountStep {
  const { user, names } = input;
  if (!user) return 'ready';

  if (isEmailAccount(user) && !emailVerified(user)) return 'verify';
  if (!input.ageConfirmed) return 'age';
  if (isEmailAccount(user) && !input.hasPassword) return 'password';
  if (!names?.first_name?.trim() || !names?.last_name?.trim()) return 'name';

  /*
   * Last, and deliberately so. Everything above it is something the account
   * cannot work without: an unverified address cannot be recovered, an
   * unconfirmed age cannot be served, a passwordless email account is stuck
   * behind a mail link, and a bartender needs a name to check against an ID.
   *
   * A handle is not in that class -- it is how other people find you, and an
   * account without one still works. It is asked for because a members' app
   * where nobody can add anybody is a worse product, not because anything
   * breaks without it. So it goes after the things that do break, and anyone
   * who already has an account meets it once on their next visit rather than
   * being held at the door on the way in.
   */
  if (!input.username?.trim()) return 'username';

  return 'ready';
}

/**
 * Which social providers this project actually has switched on.
 *
 * Asked at runtime rather than hardcoded, because a button for a provider that
 * is not enabled in the dashboard does nothing but throw, and because enabling
 * one should not need a deploy on three clients. `/auth/v1/settings` is public
 * and answers with an `external` map of provider to boolean.
 *
 * An unreachable settings endpoint returns nothing, which hides the buttons.
 * That is the right way round: a hidden button that would have worked is a
 * missing feature, a shown button that cannot work looks like a broken one.
 */
export async function enabledProviders(supabaseUrl: string, anonKey: string): Promise<string[]> {
  if (!supabaseUrl || !anonKey) return [];

  try {
    const res = await fetch(`${supabaseUrl}/auth/v1/settings`, { headers: { apikey: anonKey } });
    if (!res.ok) return [];

    const settings = (await res.json()) as { external?: Record<string, boolean> };
    return Object.entries(settings.external ?? {})
      .filter(([, on]) => on)
      .map(([id]) => id);
  } catch {
    return [];
  }
}

// ---------------------------------------------------------------- which form

export type AuthMode = 'signin' | 'signup' | 'link';

/**
 * Which form a link asked to open on.
 *
 * "Create a free account" used to land on the *sign-in* form, where the way to
 * actually create an account was a small text button under the password field.
 * Somebody who tapped a button promising a free account had to find a second
 * one to get it. A link now carries the intent — `/account?signup` — and both
 * surfaces read it here rather than each deciding for themselves, which is the
 * same reason the rest of this file exists.
 *
 * Takes whatever the router hands over: URLSearchParams on the web,
 * useLocalSearchParams' plain object on the phone. Duck-typed rather than
 * `instanceof URLSearchParams`, which is false across realms and would send
 * every link back to the sign-in form without any error to explain it.
 *
 * Presence is the signal, so `?signup`, `?signup=1` and `?signup=yes` all mean
 * the same thing — a link written by hand should not have to know which. A
 * bare `?signup` parses to the empty string rather than to null, so asking
 * what the value *is* would reject the very form these links are written in.
 */
export function authModeFrom(
  params: URLSearchParams | Record<string, unknown> | null | undefined,
  fallback: AuthMode = 'signin',
): AuthMode {
  if (!params) return fallback;

  const has = (key: string): boolean => {
    const bag = params as { has?: unknown } & Record<string, unknown>;

    // URLSearchParams.has is presence, which is what we want: `?signup` and
    // `?signup=1` both answer true, and neither is confused with absence.
    if (typeof bag.has === 'function') {
      return (bag as unknown as URLSearchParams).has(key);
    }

    // A router's plain object leaves a missing key undefined. Empty string is
    // present — that is what `?signup` becomes once parsed.
    return bag[key] !== undefined && bag[key] !== null;
  };

  // signup first: a link that somehow says both is asking for an account.
  if (has('signup')) return 'signup';
  if (has('signin')) return 'signin';
  return fallback;
}
